// Comisiones · motor de reglas — Portal Admin VentasLuga.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-comisiones.html
//
// Reglas (catálogo con editor, simulador y matriz) y Auditoría, con la banda de políticas arriba.
// La pestaña va en `?tab=`; la regla elegida vive en la pantalla. El catálogo queda montado al
// cambiar de pestaña para no perder una edición a medias.
import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { RetailCommission } from '@fiado/type-kit';
import { ConfirmDialog, usePermissions } from '@/kit';
import { cn } from '@/lib/cn';
import { PageTabs } from '@/shared/components/PageTabs';
import { KpiBar } from '@/shared/patterns/KpiBar';
import {
    countLifecycleStatuses,
    lifecycleKpiItems,
    LifecycleMasterDetail,
} from '@/shared/patterns/Lifecycle';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { useLivePayout, usePolicies, useRules } from './api/commissionQueries';
import { useCommissionDirectory } from './api/useCommissionDirectory';
import { COMMISSION_PERMISSIONS } from './lib/commissionPermissions';
import { localToday } from './lib/payoutPeriods';
import { emptyRuleForm, ruleToForm, type RuleFormValues } from './lib/ruleForm';
import {
    filterRules,
    newerRule,
    paidByRule,
    sortRules,
    STATUS_LIFECYCLE,
    type StatusFilter,
} from './lib/ruleText';
import { PoliciesBand } from './components/rules/PoliciesBand';
import { PoliciesModal } from './components/rules/PoliciesModal';
import { RuleEditor } from './components/rules/RuleEditor';
import { RuleMatrix } from './components/rules/RuleMatrix';
import { RuleSimulator } from './components/rules/RuleSimulator';
import { RulesAuditTab } from './components/rules/RulesAuditTab';
import { RulesListPanel } from './components/rules/RulesListPanel';

type Rule = RetailCommission.CommissionRule;

export type RulesTab = 'catalogo' | 'simulador' | 'matriz' | 'auditoria';
const RULES_TABS: readonly RulesTab[] = ['catalogo', 'simulador', 'matriz', 'auditoria'];

export function parseRulesTab(raw: string | undefined): RulesTab {
    return RULES_TABS.find((tab) => tab === raw) ?? 'catalogo';
}

const MAIN_TABS = [
    { key: 'reglas', label: 'Reglas' },
    { key: 'auditoria', label: 'Auditoría' },
] as const;

const SUB_TABS = [
    { key: 'catalogo', label: 'Catálogo' },
    { key: 'simulador', label: 'Simulador' },
    { key: 'matriz', label: 'Matriz' },
] as const;

/** Lo que muestra el panel derecho: una regla, una nueva (vacía o copia) o nada. */
type Selection =
    | { kind: 'rule'; ruleId: string }
    | { kind: 'new'; seed: RuleFormValues; nonce: number }
    | null;

export function PlanComisionesPage() {
    const navigate = useNavigate();
    const { tab: rawTab } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const tab = parseRulesTab(rawTab);
    const { hasPermission } = usePermissions();
    const canWrite = hasPermission(COMMISSION_PERMISSIONS.planWrite);

    const rulesQuery = useRules();
    const policies = usePolicies();
    const live = useLivePayout(undefined, hasPermission(COMMISSION_PERMISSIONS.payoutRead));
    const directory = useCommissionDirectory();

    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<StatusFilter>('ALL');
    const [selection, setSelection] = useState<Selection>(null);
    const [saved, setSaved] = useState<Rule | null>(null);
    const [dirty, setDirty] = useState(false);
    const [pending, setPending] = useState<{ next: Selection } | null>(null);
    const [editingPolicies, setEditingPolicies] = useState(false);
    const [isolate, setIsolate] = useState<string | null>(null);
    // Cada regla nueva o copia abre un editor propio.
    const [opened, setOpened] = useState(0);

    const rules = sortRules(rulesQuery.data?.rules ?? []);
    const visible = filterRules(rules, search, status);
    const paid = live.data ? paidByRule(live.data.sellers) : null;

    // La regla abierta queda fijada: buscar, filtrar o una regla nueva en la lista no la cambian.
    // Sin regla abierta, o si la abierta ya no existe, se abre la primera visible.
    const pinned = selection?.kind === 'rule' ? selection.ruleId : null;
    const firstVisible = visible.at(0)?.ruleId ?? null;
    const pinnedGone =
        pinned !== null &&
        rulesQuery.data !== undefined &&
        saved?.ruleId !== pinned &&
        !rules.some((r) => r.ruleId === pinned);
    if ((selection === null && firstVisible !== null) || pinnedGone) {
        setSelection(firstVisible === null ? null : { kind: 'rule', ruleId: firstVisible });
        setDirty(false);
    }
    // Tras guardar, la copia del motor puede ser más nueva que la de la lista.
    const selectedRule =
        pinned === null
            ? null
            : newerRule(
                  rules.find((r) => r.ruleId === pinned),
                  saved?.ruleId === pinned ? saved : null,
              );

    const goTab = (next: RulesTab) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'plan-comisiones' },
            search: (prev) => ({ ...prev, tab: next === 'catalogo' ? undefined : next }),
            replace: true,
        });
    };

    const select = (next: Selection, startsDirty = false) => {
        setSelection(next);
        setDirty(startsDirty);
    };

    /** Cambiar lo que se edita: con cambios sin guardar, primero se confirma. */
    const choose = (next: Selection) => {
        if (dirty) setPending({ next });
        else select(next);
    };

    const editorProps = {
        directory,
        policies: policies.data?.policies,
        canWrite,
        onDirtyChange: setDirty,
        onSaved: (rule: Rule) => {
            setSaved(rule);
            select({ kind: 'rule', ruleId: rule.ruleId });
        },
        // La lista todavía trae el borrador eliminado: se abre otra regla.
        onDeleted: () => {
            setSaved(null);
            const next = visible.find((r) => r.ruleId !== pinned);
            select(next ? { kind: 'rule', ruleId: next.ruleId } : null);
        },
        onCancelNew: () => {
            select(null);
        },
        // La copia lleva lo que se estaba editando, así que no hay nada que confirmar.
        onDuplicate: (seed: RuleFormValues) => {
            setOpened(opened + 1);
            select({ kind: 'new', seed, nonce: opened + 1 }, true);
        },
        onSimulate: (ruleId: string) => {
            setIsolate(ruleId);
            goTab('simulador');
        },
    };

    const newRule = canWrite
        ? () => {
              setOpened(opened + 1);
              choose({ kind: 'new', seed: emptyRuleForm(localToday()), nonce: opened + 1 });
          }
        : null;

    return (
        <ScreenFrame>
            <KpiBar
                items={lifecycleKpiItems(
                    rulesQuery.data
                        ? countLifecycleStatuses(rules.map((rule) => STATUS_LIFECYCLE[rule.status]))
                        : null,
                )}
            />
            <PageTabs
                tabs={MAIN_TABS}
                active={tab === 'auditoria' ? 'auditoria' : 'reglas'}
                onChange={(key) => {
                    goTab(key === 'auditoria' ? 'auditoria' : 'catalogo');
                }}
            />
            {tab !== 'auditoria' && (
                <>
                    <PoliciesBand
                        policies={policies.data?.policies}
                        isLoading={policies.isLoading}
                        canEdit={canWrite}
                        onEdit={() => {
                            setEditingPolicies(true);
                        }}
                    />
                    <PageTabs
                        tabs={SUB_TABS}
                        active={tab}
                        onChange={(key) => {
                            goTab(parseRulesTab(key));
                        }}
                        className="bg-muted/30"
                    />
                </>
            )}

            <LifecycleMasterDetail
                className={cn(tab !== 'catalogo' && 'hidden')}
                list={
                    <RulesListPanel
                        rules={visible}
                        isLoading={rulesQuery.isLoading}
                        error={rulesQuery.error}
                        onRetry={() => {
                            void rulesQuery.refetch();
                        }}
                        paid={paid}
                        search={search}
                        onSearch={setSearch}
                        status={status}
                        onStatus={setStatus}
                        selectedId={pinned}
                        onSelect={(ruleId) => {
                            choose({ kind: 'rule', ruleId });
                        }}
                        onNew={newRule}
                    />
                }
                detail={
                    selection?.kind === 'new' ? (
                        <RuleEditor
                            key={`new:${String(selection.nonce)}`}
                            rule={null}
                            initial={selection.seed}
                            {...editorProps}
                        />
                    ) : selectedRule ? (
                        <RuleEditor
                            key={`rule:${selectedRule.ruleId}`}
                            rule={selectedRule}
                            initial={ruleToForm(selectedRule)}
                            {...editorProps}
                        />
                    ) : (
                        <MasterDetailEmpty
                            title="Selecciona una regla"
                            sub="Elige una de la lista para verla o editarla, o crea una nueva con «Crear regla»."
                        />
                    )
                }
            />

            {/* El simulador arranca con las reglas cargadas: prende las activas al montarse. */}
            {tab === 'simulador' &&
                (rulesQuery.data ? (
                    <RuleSimulator
                        key={isolate ?? 'plan'}
                        rules={rules}
                        directory={directory}
                        isolate={isolate}
                    />
                ) : (
                    <p className="p-4 text-[12px] text-muted-foreground">
                        {rulesQuery.error
                            ? 'No se pudieron cargar las reglas.'
                            : 'Cargando reglas…'}
                    </p>
                ))}
            {tab === 'matriz' && <RuleMatrix rules={rules} />}
            {tab === 'auditoria' && <RulesAuditTab rules={rules} />}

            {editingPolicies && policies.data && (
                <PoliciesModal
                    policies={policies.data.policies}
                    onClose={() => {
                        setEditingPolicies(false);
                    }}
                />
            )}
            <ConfirmDialog
                open={pending !== null}
                onOpenChange={(open) => {
                    if (!open) setPending(null);
                }}
                title="Hay cambios sin guardar"
                description="Si sigues, se pierden los cambios de esta regla."
                confirmLabel="Descartar cambios"
                destructive
                onConfirm={() => {
                    if (pending) select(pending.next);
                    setPending(null);
                }}
            />
        </ScreenFrame>
    );
}
